/**
 * Product Dimensional Search - Gadget Proxy Version
 * Uses Gadget middleware route via Shopify App Proxy (/apps/dimensional-search)
 * which calls Shopify Admin API with server-side metafield range filtering.
 *
 *
 * Filters products based on ID, OD, and Thickness metafields with tolerance ranges.
 * LIMITED TO 500 MATCHES for performance.
 */

class ProductDimensionalSearch {
  constructor() {
    this.CACHE_KEY_BASE = 'dimensional_search_products_cache';
    this.CACHE_TTL = 10 * 60 * 1000; // 10 minutes
    this.MAX_RESULTS = 500;

    this.params = this.getSearchParams();

    const h = this.params.collectionHandle;
    const scopeKey = h && h !== 'all' ? `col:${h}` : 'catalog';
    this.CACHE_KEY = `${this.CACHE_KEY_BASE}:${scopeKey.replace(/[^a-zA-Z0-9:_-]/g, '_')}`;

    this.allProducts = [];
    this.filteredProducts = [];
    this.poolProductCount = 0;
    this.catalogFetchComplete = false;
    this.shouldSkipCacheWrite = false;
    this._lastPaintedRowCount = 0;
    this._renderDebounceTimer = null;
    this.visibleColumns = {};

    if (this.params.isDimensionalSearch) {
      this.init();
    }
  }

  getDimensionalSearchUiConfig() {
    const c = typeof window !== 'undefined' ? window.__productDimensionalSearchConfig : null;
    return {
      addToQuoteLabel: c && c.addToQuoteLabel ? c.addToQuoteLabel : 'Add to quote',
      addedLabel: 'Added',
      selectOptionsLabel: c && c.selectOptionsLabel ? c.selectOptionsLabel : 'Select options',
      outOfStockLabel: c && c.outOfStockLabel ? c.outOfStockLabel : 'Out of stock',
      atcButtonClass:
        c && c.atcButtonClass
          ? c.atcButtonClass
          : 'button button-outline button-add-to-cart',
      selectOptionsButtonClass:
        c && c.selectOptionsButtonClass
          ? c.selectOptionsButtonClass
          : 'button button-outline js-product-link'
    };
  }

  // ---------------------------------------------------------------------------
  // Event delegation for grid (qty +/- and ATC forms)
  // ---------------------------------------------------------------------------

  ensureGridEventDelegation() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid || grid.dataset.dimensionalSearchDelegated === '1') return;
    grid.dataset.dimensionalSearchDelegated = '1';
    grid.addEventListener('submit', (e) => this.handleProductGridSubmit(e));
    grid.addEventListener('click', (e) => this.handleProductGridClick(e));
  }

  dimParseQtyField(raw) {
    const d = String(raw ?? '').replace(/[^\d]/g, '');
    if (!d) return NaN;
    const n = parseInt(d, 10);
    return Number.isFinite(n) ? n : NaN;
  }

  dimSyncQtyWidth(input) {
    if (input) {
      input.removeAttribute('size');
      input.setAttribute('maxlength', '8');
    }
  }

  dimFormatQtyDisplay(input) {
    const locale = document.documentElement.lang || 'en-US';
    const maxQty = 999999;
    let n = this.dimParseQtyField(input.value);
    if (Number.isNaN(n) || !String(input.value).trim()) n = 1;
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    n = Math.max(min, Math.min(maxQty, n));
    input.value = n.toLocaleString(locale);
    this.dimSyncQtyWidth(input);
  }

  handleProductGridClick(e) {
    const dec = e.target.closest('.quantity-decrease');
    const inc = e.target.closest('.quantity-increase');
    if (!dec && !inc) return;
    const wrap = e.target.closest('.quantity-input-simple');
    if (!wrap || !document.getElementById('ProductGridContainer').contains(wrap)) return;
    const input = wrap.querySelector('.quantity-input-field');
    if (!input) return;

    if (dec) {
      const currentVal = this.dimParseQtyField(input.value);
      const n = Number.isNaN(currentVal) ? 1 : currentVal;
      if (n > 1) {
        input.value = String(n - 1);
        this.dimFormatQtyDisplay(input);
      }
      return;
    }

    if (inc) {
      const currentVal = this.dimParseQtyField(input.value);
      const n = Number.isNaN(currentVal) ? 1 : currentVal;
      input.value = String(n + 1);
      this.dimFormatQtyDisplay(input);
    }
  }

  async handleProductGridSubmit(e) {
    const form = e.target.closest('.dimensional-atc-form');
    if (!form || !document.getElementById('ProductGridContainer').contains(form)) return;
    e.preventDefault();

    const ui = this.getDimensionalSearchUiConfig();
    const variantId = form.dataset.variantId;
    const quantityInput = form.closest('tr').querySelector('.quantity-input-field');
    const qRaw = quantityInput ? this.dimParseQtyField(quantityInput.value) : NaN;
    const quantity = Number.isNaN(qRaw) ? 1 : qRaw;

    const btn = form.querySelector('.dimensional-atc-btn');
    const btnText = btn && btn.querySelector('.btn-text');
    const btnOverlay = btn && btn.querySelector('.button-overlay-spinner');

    if (!btn || !btnText || !variantId || variantId === '') return;

    if (btn.classList.contains('button-added')) {
      btnText.textContent = ui.addToQuoteLabel;
      btn.classList.remove('button-added');
    }

    btn.disabled = true;
    btn.classList.add('loading');
    if (btnOverlay) btnOverlay.classList.remove('hidden');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });

      const responseData = await response.json();

      if (response.ok) {
        const miniCart = document.querySelector('mini-cart');
        if (miniCart && miniCart.updateSectionContents) {
          await miniCart.updateSectionContents();
        }

        try {
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dimensional-search', cartData });
          }
        } catch (cartError) {
          // silent fail
        }

        if (window.themeSettings && window.themeSettings.redirectToCart) {
          window.location.href = window.routes?.cart_url || '/cart';
        } else if (miniCart && typeof miniCart.open === 'function') {
          miniCart.open(btn);
        }

        btnText.textContent = ui.addedLabel;
        btn.classList.add('button-added');
        if (quantityInput) {
          this.dimFormatQtyDisplay(quantityInput);
        }
      } else {
        throw new Error(responseData.description || 'Failed to add to cart');
      }
    } catch (error) {
      btnText.textContent = 'Error';
      setTimeout(() => {
        btnText.textContent = ui.addToQuoteLabel;
      }, 2000);
    } finally {
      btn.classList.remove('loading');
      if (btnOverlay) btnOverlay.classList.add('hidden');
      btn.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // URL / param helpers
  // ---------------------------------------------------------------------------

  getSearchParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      isDimensionalSearch: urlParams.get('dimensional_search') === 'true',
      searchUnit: urlParams.get('search_unit') || 'in',
      insideDiameterNom: this.parseFloat(urlParams.get('inside_diameter_nom')),
      insideDiameterTol: this.parseFloat(urlParams.get('inside_diameter_tol')),
      outsideDiameterNom: this.parseFloat(urlParams.get('outside_diameter_nom')),
      outsideDiameterTol: this.parseFloat(urlParams.get('outside_diameter_tol')),
      thicknessNom: this.parseFloat(urlParams.get('thickness_nom')),
      thicknessTol: this.parseFloat(urlParams.get('thickness_tol')),
      collectionHandle: this.getCollectionHandleFromPath()
    };
  }

  getCollectionHandleFromPath() {
    const pathname = window.location.pathname;
    const prefix = '/collections/';
    const idx = pathname.indexOf(prefix);
    if (idx === -1) return null;
    const rest = pathname.slice(idx + prefix.length);
    const segment = rest.split('/')[0];
    return segment ? decodeURIComponent(segment) : null;
  }

  getCollectionBasePath() {
    const h = this.params.collectionHandle;
    if (h) return `/collections/${encodeURIComponent(h)}`;
    return '/collections/all';
  }

  isCollectionScopedSearch() {
    const h = this.params.collectionHandle;
    return Boolean(h && h !== 'all');
  }

  parseFloat(value) {
    if (!value || value.trim() === '') return null;
    const parsed = window.parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }

  hasDimensionalSearchCriteria() {
    const p = this.params;
    return p.insideDiameterNom !== null || p.outsideDiameterNom !== null || p.thicknessNom !== null;
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  hasInvalidIdOd() {
    const p = this.params;
    return (
      p.insideDiameterNom !== null &&
      p.outsideDiameterNom !== null &&
      p.insideDiameterNom >= p.outsideDiameterNom
    );
  }

  async init() {
    if (this.hasInvalidIdOd()) {
      this.showErrorMessage('ID must be smaller than OD.');
      return;
    }

    this.showLoadingState();
    this.updatePageTitle();
    this.ensureGridEventDelegation();
    this.hideFiltersAndDisableToggle();
    this.showSearchSummary();

    try {
      const loadMode = await this.fetchAllProducts();

      if (loadMode === 'cached') {
        if (this.allProducts.length === 0) {
          this.hideLoadingState();
          this.showNoProductsFoundError();
          return;
        }
        this.poolProductCount = this.allProducts.length;
        this.catalogFetchComplete = true;
        this.filterProducts();
        this.determineVisibleColumns();
        this._lastPaintedRowCount = 0;
        this.renderResults();
        this.hideLoadingState();
        this.showSearchSummary();
        this.initializeAddToCart();
        return;
      }

      // Streaming path
      if (this.filteredProducts.length === 0) {
        this.hideLoadingState();
        if (this.poolProductCount === 0) {
          this.showNoProductsFoundError();
        } else {
          const container = document.getElementById('ProductGridContainer');
          if (container) {
            container.innerHTML = `
              <div class="dimensional-search-no-results">
                <h2>No products found</h2>
                <p>No washers match your dimensional specifications. Try adjusting your tolerances or search criteria.</p>
              </div>
            `;
          }
        }
        this.showSearchSummary();
        return;
      }

      this.determineVisibleColumns();
      this.flushIncrementalRender(true);
      this.hideLoadingState();
      this.showSearchSummary();
      this.initializeAddToCart();

    } catch (error) {
      this.hideLoadingState();
      const message =
        error && typeof error.message === 'string' && error.message.trim() !== ''
          ? error.message
          : 'An error occurred while searching. Please try again.';
      this.showErrorMessage(message);
    }
  }

  // Hides the collection-actions bar (sort + filter button), collapses the
  // filter sidebar, and disables the filters toggle button while a dimensional
  // search is active — the native sort doesn't work against our custom result
  // set. Clearing the search reloads the page (via the "Clear Search" link),
  // which restores the normal sidebar/bar state.
  hideFiltersAndDisableToggle() {
    const collectionActions = document.getElementById('CollectionActions');
    const stickyActions = document.querySelector('sticky-collection-actions');
    const facetsWrap = document.querySelector('.facets-wrap');
    const sidebar = document.querySelector('.page-layout-sidebar');
    const toggleButton = document.querySelector('.button-collection-filters-toggle');

    if (collectionActions) collectionActions.hidden = true;
    if (stickyActions) stickyActions.hidden = true;

    const productCount = document.getElementById('ProductCount');
    if (productCount) productCount.hidden = true;

    if (facetsWrap) facetsWrap.classList.remove('page-layout-with-sidebar');
    if (sidebar) sidebar.classList.add('is-collapsed');

    if (toggleButton) {
      toggleButton.disabled = true;
      toggleButton.setAttribute('aria-disabled', 'true');
      toggleButton.classList.remove('is-expanded');
      toggleButton.title = 'Clear search to see filters in this collection';

      const label = toggleButton.querySelector('span');
      if (label) label.textContent = 'Clear search to see filters in this collection';
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch — single pagination loop for both collection + catalog
  // Gadget route handles collection filtering server-side via collectionHandle param
  // ---------------------------------------------------------------------------

  async fetchAllProducts() {
    this.poolProductCount = 0;
    this.catalogFetchComplete = false;
    this.shouldSkipCacheWrite = false;
    this.filteredProducts = [];
    this._lastPaintedRowCount = 0;
    if (this._renderDebounceTimer) {
      clearTimeout(this._renderDebounceTimer);
      this._renderDebounceTimer = null;
    }

    const cached = this.getCachedProducts();
    if (cached) {
      this.allProducts = cached;
      return 'cached';
    }

    this.allProducts = [];

    let hasNextPage = true;
    let cursor = null;
    let batchCount = 0;
    const matchCap = this.MAX_RESULTS;

    while (hasNextPage) {
      batchCount += 1;

      if (this.filteredProducts.length >= matchCap) {
        this.shouldSkipCacheWrite = true;
        break;
      }

      const data = await this.fetchProductBatch(cursor);

      if (!data || !data.products) {
        break;
      }

      const products = data.products;
      const edges = products.edges || [];

      // Since Gadget/Admin API already filtered by dimensions server-side,
      // all returned products are matches — just map and push them
      for (const edge of edges) {
        if (this.filteredProducts.length >= matchCap) {
          this.shouldSkipCacheWrite = true;
          break;
        }
        this.processNode(edge.node);
      }

      if (this.filteredProducts.length >= matchCap) {
        this.shouldSkipCacheWrite = true;
        break;
      }

      hasNextPage = products.pageInfo?.hasNextPage || false;
      cursor = products.pageInfo?.endCursor || null;

      if (batchCount > 100) {
        break;
      }
    }

    this.catalogFetchComplete = true;

    return 'stream';
  }

  // ---------------------------------------------------------------------------
  // Gadget proxy fetch — single method, handles both collection + catalog
  // ---------------------------------------------------------------------------

  async fetchProductBatch(cursor) {
    const p = this.params;

    const url = new URL('/apps/swgc-dimensional-search', window.location.origin);

    // Collection scoping — Gadget filters by collection server-side
    if (p.collectionHandle && p.collectionHandle !== 'all') {
      url.searchParams.set('collectionHandle', p.collectionHandle);
    }

    // Dimension range params
    if (p.insideDiameterNom  !== null) url.searchParams.set('id_nom',  p.insideDiameterNom);
    if (p.insideDiameterTol  !== null) url.searchParams.set('id_tol',  p.insideDiameterTol);
    if (p.outsideDiameterNom !== null) url.searchParams.set('od_nom',  p.outsideDiameterNom);
    if (p.outsideDiameterTol !== null) url.searchParams.set('od_tol',  p.outsideDiameterTol);
    if (p.thicknessNom       !== null) url.searchParams.set('thk_nom', p.thicknessNom);
    if (p.thicknessTol       !== null) url.searchParams.set('thk_tol', p.thicknessTol);
    url.searchParams.set('unit', p.searchUnit);
    if (cursor) url.searchParams.set('cursor', cursor);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Proxy request failed with status ${response.status}`);
    }

    const json = await response.json();

    if (json.error) {
      throw new Error(json.error);
    }

    // Remap Admin API price shape → Storefront shape
    // Admin API: price is a plain string "1.99"
    // Storefront API (what mapNodeToProduct expects): price is { amount: "1.99" }
    if (json.products?.edges) {
      json.products.edges = json.products.edges.map(edge => ({
        ...edge,
        node: {
          ...edge.node,
          variants: {
            edges: (edge.node.variants?.edges || []).map(v => ({
              node: {
                ...v.node,
                price: { amount: v.node.price ?? '0' },
                compareAtPrice: v.node.compareAtPrice ? { amount: v.node.compareAtPrice } : null
              }
            }))
          }
        }
      }));
    }

    return { products: json.products };
  }

  // ---------------------------------------------------------------------------
  // Node processing — all nodes from Gadget are already filtered matches
  // ---------------------------------------------------------------------------

  processNode(node) {
    this.poolProductCount += 1;
    const product = this.mapNodeToProduct(node);
    this.filteredProducts.push(product);
    this.afterFilteredProductAdded();
  }

  afterFilteredProductAdded() {
    if (this.filteredProducts.length === 1) {
      if (this._renderDebounceTimer) {
        clearTimeout(this._renderDebounceTimer);
        this._renderDebounceTimer = null;
      }
      this.flushIncrementalRender(false);
      return;
    }
    this.scheduleIncrementalRender();
  }

  scheduleIncrementalRender() {
    if (this._renderDebounceTimer) {
      clearTimeout(this._renderDebounceTimer);
    }
    this._renderDebounceTimer = setTimeout(() => {
      this._renderDebounceTimer = null;
      this.flushIncrementalRender(false);
    }, 72);
  }

  flushIncrementalRender(forceFinal) {
    if (forceFinal && this._renderDebounceTimer) {
      clearTimeout(this._renderDebounceTimer);
      this._renderDebounceTimer = null;
    }

    const n = this.filteredProducts.length;
    if (!forceFinal && n === this._lastPaintedRowCount) return;

    this.determineVisibleColumns();

    const container = document.getElementById('ProductGridContainer');
    if (!container || n === 0) return;

    this.renderResults();
    this._lastPaintedRowCount = n;
    this.refreshSearchSummaryCounts();
    this.initializeAddToCart();
  }

  // ---------------------------------------------------------------------------
  // Product mapping
  // ---------------------------------------------------------------------------

  mapNodeToProduct(node) {
    const variantEdges = node.variants && node.variants.edges ? node.variants.edges : [];
    return {
      id: node.id,
      handle: node.handle,
      title: node.title,
      url: `/products/${node.handle}`,
      availableForSale: node.availableForSale,
      variants: variantEdges.map(v => ({
        id: v.node.id,
        title: v.node.title,
        availableForSale: v.node.availableForSale,
        price: v.node.price?.amount || '0',
        compareAtPrice: v.node.compareAtPrice?.amount || null
      })),
      metafields: {
        id: this.parseFloat(node.id_in?.value),
        od: this.parseFloat(node.od_in?.value),
        thk: this.parseFloat(node.thk_in?.value),
        id_mm: this.parseFloat(node.id_mm?.value),
        od_mm: this.parseFloat(node.od_mm?.value),
        thk_mm: this.parseFloat(node.thk_mm?.value),
        material: node.material?.value || null,
        size: node.size?.value || null,
        id_tol2: node.id_tol2?.value || null,
        od_tol2: node.od_tol2?.value || null,
        drawing: node.drawing?.value || null,
        aa: node.aa?.value || null,
        bb: node.bb?.value || null,
        cc: node.cc?.value || null,
        id_mm_tol: node.id_mm_tol?.value || null,
        od_mm_tol: node.od_mm_tol?.value || null,
        length_tol: node.length_tol?.value || null,
        length: node.length?.value || null,
        free_h_tol: node.free_h_tol?.value || null,
        free_h: node.free_h?.value || null
      }
    };
  }

  // ---------------------------------------------------------------------------
  // Client-side filter (used only for cached results)
  // Live results from Gadget are pre-filtered server-side
  // ---------------------------------------------------------------------------

  filterProducts() {
    const {
      insideDiameterNom, insideDiameterTol,
      outsideDiameterNom, outsideDiameterTol,
      thicknessNom, thicknessTol,
      searchUnit
    } = this.params;

    const hasSearchCriteria =
      insideDiameterNom !== null || outsideDiameterNom !== null || thicknessNom !== null;

    if (!hasSearchCriteria) {
      this.filteredProducts = this.allProducts.slice(0, this.MAX_RESULTS);
      return;
    }

    for (const product of this.allProducts) {
      if (this.filteredProducts.length >= this.MAX_RESULTS) break;

      const m = product.metafields;
      const idValue  = searchUnit === 'mm' ? m.id_mm  : m.id;
      const odValue  = searchUnit === 'mm' ? m.od_mm  : m.od;
      const thkValue = searchUnit === 'mm' ? m.thk_mm : m.thk;

      if (
        this.isWithinTolerance(idValue,  insideDiameterNom,  insideDiameterTol) &&
        this.isWithinTolerance(odValue,  outsideDiameterNom, outsideDiameterTol) &&
        this.isWithinTolerance(thkValue, thicknessNom,       thicknessTol)
      ) {
        this.filteredProducts.push(product);
      }
    }
  }

  isWithinTolerance(value, nominal, tolerance) {
    if (nominal === null) return true;
    if (value === null) return false;
    const tol = tolerance !== null ? tolerance : 0;
    return value >= nominal - tol && value <= nominal + tol;
  }

  // ---------------------------------------------------------------------------
  // Cache
  // ---------------------------------------------------------------------------

  cacheProducts(products) {
    try {
      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        products
      }));
    } catch (e) {
      // silent fail — cache write is non-critical
    }
  }

  getCachedProducts() {
    try {
      const cached = sessionStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;
      const cacheData = JSON.parse(cached);
      const age = Date.now() - cacheData.timestamp;
      if (age > this.CACHE_TTL) {
        sessionStorage.removeItem(this.CACHE_KEY);
        return null;
      }
      return cacheData.products;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  refreshSearchSummaryCounts() {
    const span = document.querySelector('.dimensional-search-summary .search-result-count');
    if (!span) return;
    span.textContent = this.buildResultCountText();
  }

  // Single source of truth for the summary count text. Returns:
  //  - "Calculating total products…" before any results have arrived
  //  - "<n> products match[ in this collection] (searching…)" while streaming
  //  - "<n> products match[ in this collection]" once the fetch is complete
  //  - When the result set was capped at MAX_RESULTS, appends a hint asking
  //    the shopper to tighten their tolerances for more precise results.
  buildResultCountText() {
    const count = this.filteredProducts.length;
    const suffix = this.isCollectionScopedSearch() ? ' in this collection' : '';

    if (count === 0 && !this.catalogFetchComplete) {
      return 'Searching…';
    }

    const verb = count === 1 ? 'matches' : 'match';
    const noun = count === 1 ? 'product' : 'products';
    const searching = !this.catalogFetchComplete ? ' (searching…)' : '';
    const capped = this.catalogFetchComplete && count >= this.MAX_RESULTS;
    const displayCount = capped ? `${this.MAX_RESULTS.toLocaleString()}+` : `${count}`;
    const cappedHint = capped
      ? ' — try narrowing your tolerances for more precise results'
      : '';
    return `${displayCount} ${noun} ${verb}${suffix}${searching}${cappedHint}`;
  }

  determineVisibleColumns() {
    this.visibleColumns = {
      sku: true, id: true, od: true, thickness: true, quantity: true, action: true
    };

    const optionalColumns = [
      'material', 'size', 'id_tol2', 'od_tol2', 'drawing',
      'aa', 'bb', 'cc', 'id_mm_tol', 'od_mm_tol',
      'length_tol', 'length', 'free_h_tol', 'free_h'
    ];

    optionalColumns.forEach(col => {
      this.visibleColumns[col] = this.filteredProducts.some(product => {
        const value = product.metafields[col];
        return value !== null && value !== undefined && value !== '';
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  /** PLP row cell markup (label + value) for mobile grid parity with product-rows-table */
  renderDimSpecCell(label, value, cellClass = 'col-dim') {
    const display =
      value === null || value === undefined || value === '' ? '—' : String(value);
    return `<td class="table-cell ${cellClass}">
      <div class="product-metafield-item">
        <span class="product-metafield-label">${label}</span>
        <span class="product-metafield-value">${display}</span>
      </div>
    </td>`;
  }

  renderResults() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    this.ensureGridEventDelegation();

    const { searchUnit } = this.params;
    const unitLabel = searchUnit === 'mm' ? 'mm' : '"';
    const useMetric = searchUnit === 'mm';
    const ui = this.getDimensionalSearchUiConfig();

    if (this.filteredProducts.length === 0) {
      container.innerHTML = `
        <div class="dimensional-search-no-results">
          <h2>No products found</h2>
          <p>No washers match your dimensional specifications. Try adjusting your tolerances or search criteria.</p>
        </div>
      `;
      return;
    }

    let html = `
      <div class="dimensional-results-wrapper">
        <div class="dimensional-results-table-wrapper">
          <table class="dimensional-results-table">
            <thead>
              <tr>
                <th class="col-sku">SKU</th>
                <th class="col-dim">ID${useMetric ? ' (mm)' : ' (in)'}</th>
                <th class="col-dim">OD${useMetric ? ' (mm)' : ' (in)'}</th>
                <th class="col-dim">Thk${useMetric ? ' (mm)' : ' (in)'}</th>
                ${this.visibleColumns.material  ? '<th class="col-text">Material</th>'    : ''}
                ${this.visibleColumns.size      ? '<th class="col-text">Size</th>'        : ''}
                ${this.visibleColumns.id_tol2   ? '<th class="col-dim">ID TOL</th>'       : ''}
                ${this.visibleColumns.od_tol2   ? '<th class="col-dim">OD TOL</th>'       : ''}
                ${this.visibleColumns.drawing   ? '<th class="col-text">Drawing</th>'     : ''}
                ${this.visibleColumns.aa        ? '<th class="col-dim">A</th>'            : ''}
                ${this.visibleColumns.bb        ? '<th class="col-dim">B</th>'            : ''}
                ${this.visibleColumns.cc        ? '<th class="col-dim">C</th>'            : ''}
                ${this.visibleColumns.id_mm_tol ? '<th class="col-dim">ID MM TOL</th>'   : ''}
                ${this.visibleColumns.od_mm_tol ? '<th class="col-dim">OD MM TOL</th>'   : ''}
                ${this.visibleColumns.length_tol? '<th class="col-dim">Length TOL</th>'  : ''}
                ${this.visibleColumns.length    ? '<th class="col-dim">Length</th>'       : ''}
                ${this.visibleColumns.free_h_tol? '<th class="col-dim">Free H TOL</th>'  : ''}
                ${this.visibleColumns.free_h    ? '<th class="col-dim">Free H</th>'       : ''}
                <th class="col-quantity">Qty</th>
                <th class="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
    `;

    for (const product of this.filteredProducts) {
      const m = product.metafields;
      const idVal  = useMetric ? m.id_mm  : m.id;
      const odVal  = useMetric ? m.od_mm  : m.od;
      const thkVal = useMetric ? m.thk_mm : m.thk;

      const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
      const variantId = firstVariant && firstVariant.id ? firstVariant.id.split('/').pop() : '';
      const isAvailable = firstVariant ? firstVariant.availableForSale : false;

      const idDisplay = idVal !== null ? idVal + unitLabel : null;
      const odDisplay = odVal !== null ? odVal + unitLabel : null;
      const thkDisplay = thkVal !== null ? thkVal + unitLabel : null;
      const unitSuffix = useMetric ? ' (mm)' : ' (in)';

      html += `
        <tr class="product-row-table dimensional-result-row" data-product-handle="${product.handle}">
          <td class="table-cell table-cell-sku col-sku">
            <h3 class="row-heading">
              <a href="${product.url}" class="dimensional-result-link">${product.title}</a>
            </h3>
          </td>
          ${this.renderDimSpecCell('ID' + unitSuffix, idDisplay)}
          ${this.renderDimSpecCell('OD' + unitSuffix, odDisplay)}
          ${this.renderDimSpecCell('Thk' + unitSuffix, thkDisplay)}
          ${this.visibleColumns.material ? this.renderDimSpecCell('Material', m.material, 'col-text table-cell-material') : ''}
          ${this.visibleColumns.size ? this.renderDimSpecCell('Size', m.size, 'col-text table-cell-size') : ''}
          ${this.visibleColumns.id_tol2 ? this.renderDimSpecCell('ID TOL', m.id_tol2) : ''}
          ${this.visibleColumns.od_tol2 ? this.renderDimSpecCell('OD TOL', m.od_tol2) : ''}
          ${this.visibleColumns.drawing ? this.renderDimSpecCell('Drawing', m.drawing, 'col-text') : ''}
          ${this.visibleColumns.aa ? this.renderDimSpecCell('A', m.aa) : ''}
          ${this.visibleColumns.bb ? this.renderDimSpecCell('B', m.bb) : ''}
          ${this.visibleColumns.cc ? this.renderDimSpecCell('C', m.cc) : ''}
          ${this.visibleColumns.id_mm_tol ? this.renderDimSpecCell('ID MM TOL', m.id_mm_tol) : ''}
          ${this.visibleColumns.od_mm_tol ? this.renderDimSpecCell('OD MM TOL', m.od_mm_tol) : ''}
          ${this.visibleColumns.length_tol ? this.renderDimSpecCell('Length TOL', m.length_tol) : ''}
          ${this.visibleColumns.length ? this.renderDimSpecCell('Length', m.length) : ''}
          ${this.visibleColumns.free_h_tol ? this.renderDimSpecCell('Free H TOL', m.free_h_tol) : ''}
          ${this.visibleColumns.free_h ? this.renderDimSpecCell('Free H', m.free_h) : ''}
          <td class="table-cell table-cell-quantity col-quantity">
            <div class="product-metafield-item product-metafield-item--quantity">
              <span class="product-metafield-label">Quantity</span>
              <span class="product-metafield-value">
                ${
                  isAvailable
                    ? `<div class="quantity-input quantity-input-simple" data-product-handle="${product.handle}">
                <button type="button" class="quantity-input-button quantity-input-minus quantity-btn quantity-decrease" aria-label="Decrease quantity">&minus;</button>
                <input type="number" class="quantity-input-field" value="1" min="1" step="1" aria-label="Quantity">
                <button type="button" class="quantity-input-button quantity-input-plus quantity-btn quantity-increase" aria-label="Increase quantity">&plus;</button>
              </div>`
                    : '—'
                }
              </span>
            </div>
          </td>
          <td class="table-cell table-cell-actions col-action">
            ${
              product.variants.length > 1
                ? `<div class="product-actions card-product-actions">
              <a href="${product.url}" class="${ui.selectOptionsButtonClass}">${ui.selectOptionsLabel}</a>
            </div>`
                : isAvailable
                  ? `<product-form>
              <form class="dimensional-atc-form shopify-product-form" data-variant-id="${variantId}" data-product-handle="${product.handle}" data-type="add-to-cart-form">
                <div class="product-actions card-product-actions product-actions-add-to-cart">
                  <button type="submit" name="add" class="${ui.atcButtonClass} dimensional-atc-btn" aria-haspopup="dialog">
                    <span class="btn-text">${ui.addToQuoteLabel}</span>
                    <div class="button-overlay-spinner hidden"><span class="spinner-xs"></span></div>
                  </button>
                </div>
              </form>
            </product-form>`
                  : `<div class="product-actions card-product-actions">
              <button type="button" class="${ui.atcButtonClass}" disabled><span>${ui.outOfStockLabel}</span></button>
            </div>`
            }
          </td>
        </tr>
      `;
    }

    html += `</tbody></table></div></div>`;
    container.innerHTML = html;
  }

  initializeAddToCart() {
    document.querySelectorAll('#ProductGridContainer .dimensional-results-table .quantity-input-field').forEach((input) => {
      if (input.dataset.swQtyFormat === '1') return;
      input.dataset.swQtyFormat = '1';
      input.type = 'text';
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('autocomplete', 'off');
      input.addEventListener('focus', () => {
        const n = this.dimParseQtyField(input.value);
        if (!Number.isNaN(n)) input.value = String(n);
        this.dimSyncQtyWidth(input);
      });
      input.addEventListener('blur', () => { this.dimFormatQtyDisplay(input); });
      this.dimFormatQtyDisplay(input);
    });
  }

  showLoadingState() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    container.innerHTML = `
      <div class="dimensional-search-loading-state">
        <div class="dimensional-search-spinner"></div>
        <p>${this.isCollectionScopedSearch() ? 'Searching products in this collection…' : 'Searching all products by dimensions…'}</p>
      </div>
    `;
  }

  hideLoadingState() {
    // No-op — renderResults replaces content
  }

  updatePageTitle() {
    const pageTitle = document.querySelector('.page-title, .collection-hero-heading, h1');
    if (pageTitle) pageTitle.textContent = 'Washer Search Results';
  }

  showSearchSummary() {
    const {
      insideDiameterNom, insideDiameterTol,
      outsideDiameterNom, outsideDiameterTol,
      thicknessNom, thicknessTol,
      searchUnit
    } = this.params;

    const unitLabel = searchUnit === 'mm' ? 'mm' : '"';
    const decimals = 3;

    let summaryElement = document.querySelector('.dimensional-search-summary');
    if (!summaryElement) {
      summaryElement = document.createElement('div');
      summaryElement.className = 'dimensional-search-summary';
      const mainContent = document.querySelector('.page-layout-main-content');
      const resultsContainer = document.getElementById('ProductGridContainer');
      if (mainContent && resultsContainer) {
        mainContent.insertBefore(summaryElement, resultsContainer);
      }
    }

    let criteriaHTML = '';
    if (insideDiameterNom !== null) {
      const min = insideDiameterNom - (insideDiameterTol || 0);
      const max = insideDiameterNom + (insideDiameterTol || 0);
      criteriaHTML += `<span class="search-criteria-tag">ID: ${min.toFixed(decimals)}${unitLabel} – ${max.toFixed(decimals)}${unitLabel}</span>`;
    }
    if (outsideDiameterNom !== null) {
      const min = outsideDiameterNom - (outsideDiameterTol || 0);
      const max = outsideDiameterNom + (outsideDiameterTol || 0);
      criteriaHTML += `<span class="search-criteria-tag">OD: ${min.toFixed(decimals)}${unitLabel} – ${max.toFixed(decimals)}${unitLabel}</span>`;
    }
    if (thicknessNom !== null) {
      const min = thicknessNom - (thicknessTol || 0);
      const max = thicknessNom + (thicknessTol || 0);
      criteriaHTML += `<span class="search-criteria-tag">THK: ${min.toFixed(decimals)}${unitLabel} – ${max.toFixed(decimals)}${unitLabel}</span>`;
    }

    const clearHref = this.getCollectionBasePath();

    summaryElement.innerHTML = `
      <div class="dimensional-search-summary-inner">
        <div class="search-criteria-list">
          <strong>Search Criteria:</strong>
          ${criteriaHTML}
          <span class="search-criteria-tag search-criteria-unit">Unit: ${searchUnit.toUpperCase()}</span>
        </div>
        <div class="search-summary-actions">
          <span class="search-result-count">${this.buildResultCountText()}</span>
          <a href="${clearHref}" class="dimensional-search-clear-btn">Clear Search</a>
        </div>
      </div>
    `;
  }

  showNoProductsFoundError() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const href = this.getCollectionBasePath();
    const cta = this.isCollectionScopedSearch() ? 'Back to collection' : 'Go to All Products';
    container.innerHTML = `
      <div class="dimensional-search-no-results">
        <h2>No Products Found</h2>
        <p>No washers match your dimensional specifications. Try adjusting your tolerances or search criteria.</p>
        <a href="${href}" class="dimensional-result-btn">${cta}</a>
      </div>
    `;
  }

  showErrorMessage(message) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const href = this.getCollectionBasePath();
    const cta = this.isCollectionScopedSearch() ? 'Back to collection' : 'Go to All Products';
    container.innerHTML = `
      <div class="dimensional-search-no-results">
        <h2>Error</h2>
        <p>${message}</p>
        <a href="${href}" class="dimensional-result-btn">${cta}</a>
      </div>
    `;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { new ProductDimensionalSearch(); });
} else {
  new ProductDimensionalSearch();
}